import { Injectable } from '@angular/core';

import { getTimes } from 'src/app/features/timemanager/services';

import { RemoteDiagnosticsVddGenericStructure } from '@vi-iot/client';
import { SemanticValues, StartStopTempData, TimeType } from './diagnostics-constants';

@Injectable({ providedIn: 'root' })
export class DiagnosticsVddStartStopTempService {
  defaultMode = 'Nothing';
  defaultStartTime = '00:00';
  defaultStopTime = '24:00';
  isMaxNumberOfTimePhasesReached = false;

  getHourMinuteStructure(
    structure: RemoteDiagnosticsVddGenericStructure
  ): {
    startHourStructure: RemoteDiagnosticsVddGenericStructure;
    startMinuteStructure: RemoteDiagnosticsVddGenericStructure;
    stopHourStructure: RemoteDiagnosticsVddGenericStructure;
    stopMinuteStructure: RemoteDiagnosticsVddGenericStructure;
  } {
    const startTime = this.getStructure(structure, SemanticValues.STARTTIME);
    const stopTime = this.getStructure(structure, SemanticValues.STOPTIME);

    return {
      startHourStructure: this.getStructure(startTime, SemanticValues.HOUR),
      startMinuteStructure: this.getStructure(startTime, SemanticValues.MINUTE),
      stopHourStructure: this.getStructure(stopTime, SemanticValues.HOUR),
      stopMinuteStructure: this.getStructure(stopTime, SemanticValues.MINUTE),
    };
  }

  getStartStopHourMinuteValues(
    startTime: string,
    stopTime: string
  ): {
    startHour: string;
    startMinute: string;
    stopHour: string;
    stopMinute: string;
  } {
    const start = this.getHourMinuteValues(startTime);
    const stop = this.getHourMinuteValues(stopTime);

    return {
      startHour: start.hour,
      startMinute: start.minute,
      stopHour: stop.hour,
      stopMinute: stop.minute,
    };
  }

  getStructure(
    structure: RemoteDiagnosticsVddGenericStructure,
    semantic: SemanticValues
  ): RemoteDiagnosticsVddGenericStructure {
    return structure.structure.find((element) => element.semantic === semantic);
  }

  getHourMinuteValues(
    time: string
  ): {
    hour: string;
    minute: string;
  } {
    const [hour, minute] = time.split(':').map((t) => (t[0] === '0' ? t[1] : t));

    return { hour, minute };
  }

  getStartStopTime(
    structure: RemoteDiagnosticsVddGenericStructure
  ): {
    startTime: string;
    stopTime: string;
  } {
    const startTime = this.getTimeValueFromStructure(
      this.getStructure(structure, SemanticValues.STARTTIME),
      TimeType.StartTime
    );
    const stopTime = this.getTimeValueFromStructure(
      this.getStructure(structure, SemanticValues.STOPTIME),
      TimeType.StopTime
    );

    return { startTime, stopTime };
  }

  getTimeValueFromStructure(
    value: RemoteDiagnosticsVddGenericStructure,
    timeType?: string
  ): string {
    let hourValue = this.getStructure(value, SemanticValues.HOUR)?.value;
    let minuteValue = this.getStructure(value, SemanticValues.MINUTE)?.value;

    if (timeType === TimeType.StopTime && hourValue === '0' && minuteValue === '0') {
      return this.defaultStopTime;
    } else if (!!hourValue && !!minuteValue) {
      if (Number(hourValue) <= 9) {
        hourValue = `0${hourValue}`;
      }
      if (Number(minuteValue) <= 9) {
        minuteValue = `0${minuteValue}`;
      }

      return `${hourValue}:${minuteValue}`;
    }

    return this.defaultStartTime;
  }

  getStartTimes(structure: RemoteDiagnosticsVddGenericStructure, timeType: TimeType): string[] {
    return getTimes(
      this.defaultStartTime,
      this.getTimeValueFromStructure(
        this.getStructure(structure, SemanticValues.STOPTIME),
        timeType
      ),
      this.getTimeValueFromStructure(this.getStructure(structure, SemanticValues.STOPTIME))
    );
  }

  getStopTimes(structure: RemoteDiagnosticsVddGenericStructure): string[] {
    return getTimes(
      this.getTimeValueFromStructure(this.getStructure(structure, SemanticValues.STARTTIME)),
      this.defaultStopTime,
      this.getTimeValueFromStructure(this.getStructure(structure, SemanticValues.STARTTIME))
    );
  }

  recalculateTimePhasesByStartTimeChange(
    newTime: string,
    timePhaseIndex: number,
    timePhases: RemoteDiagnosticsVddGenericStructure[],
    entriesStructure: RemoteDiagnosticsVddGenericStructure
  ): StartStopTempData {
    let entries = Number(entriesStructure.value);
    const updatedTimePhases = timePhases.map((structure, idx) => {
      const startStopTime = this.getStartStopTime(structure);
      const hourMinuteStructure = this.getHourMinuteStructure(structure);
      const hourMinuteValues = this.getHourMinuteValues(newTime);

      // Change the start time of the edited time phase
      if (timePhaseIndex === idx) {
        hourMinuteStructure.startHourStructure.value = hourMinuteValues.hour;
        hourMinuteStructure.startMinuteStructure.value = hourMinuteValues.minute;
      }

      // Ignore all higher phases than the edited one
      if (idx < timePhaseIndex) {
        // Update stop time of previous time phase if new time is between the start and end of the previous time phase
        if (newTime > startStopTime.startTime && newTime < startStopTime.stopTime) {
          hourMinuteStructure.stopHourStructure.value = hourMinuteValues.hour;
          hourMinuteStructure.stopMinuteStructure.value = hourMinuteValues.minute;
        }

        // Delete the previous time phases if the new time is lower or equal to the start time
        // and lower than the stop time of the previous time phase
        if (newTime <= startStopTime.startTime && newTime < startStopTime.stopTime) {
          entries = entries - 1;

          return this.clearTimePhase(structure);
        }
      }

      return structure;
    });

    return {
      structure: this.sortTimePhasesByStartTime(updatedTimePhases),
      entriesValue: entries.toString(),
    };
  }

  recalculateTimePhasesByStopTimeChange(
    newTime: string,
    timePhaseIndex: number,
    timePhases: RemoteDiagnosticsVddGenericStructure[],
    entriesStructure: RemoteDiagnosticsVddGenericStructure
  ): StartStopTempData {
    let entries = Number(entriesStructure.value);
    const updatedTimePhases = timePhases.map((structure, idx) => {
      const stopTimeStructure = this.getStructure(structure, SemanticValues.STOPTIME);
      const startStopTime = this.getStartStopTime(structure);
      const hourMinuteStructure = this.getHourMinuteStructure(structure);
      const hourMinuteValues = this.getHourMinuteValues(newTime);
      const stopTime = this.getTimeValueFromStructure(stopTimeStructure);

      // Change the stop time of the edited time phase
      if (timePhaseIndex === idx) {
        hourMinuteStructure.stopHourStructure.value = hourMinuteValues.hour;
        hourMinuteStructure.stopMinuteStructure.value = hourMinuteValues.minute;
      }

      // Ignore empty time phases (default value is 00:00 for stop time)
      // and all lower phases than the edited one
      if (stopTime !== this.defaultStartTime && idx > timePhaseIndex) {
        // Update start time of next time phase if new time is between the start and stop of the next time phase
        if (newTime > startStopTime.startTime && newTime < startStopTime.stopTime) {
          hourMinuteStructure.startHourStructure.value = hourMinuteValues.hour;
          hourMinuteStructure.startMinuteStructure.value = hourMinuteValues.minute;
        }

        // Delete the next time phase if the new time is higher than the start time
        // and higher or equal to the stop time of the next time phase
        if (newTime > startStopTime.startTime && newTime >= startStopTime.stopTime) {
          entries = entries - 1;

          return this.clearTimePhase(structure);
        }
      }

      return structure;
    });

    return {
      structure: this.sortTimePhasesByStartTime(updatedTimePhases),
      entriesValue: entries.toString(),
    };
  }

  findAndUpdateModeStructure(
    newValue: string,
    timePhaseIndex: number,
    timePhases: RemoteDiagnosticsVddGenericStructure[]
  ): RemoteDiagnosticsVddGenericStructure[] {
    const updatedTimePhases = timePhases.map((structure, idx) => {
      if (timePhaseIndex === idx) {
        const modeStructure = this.getStructure(structure, SemanticValues.ENUMCHOICE);
        modeStructure.value = newValue;
      }

      return structure;
    });

    return updatedTimePhases;
  }

  recalculateTimePhasesByAddOne(
    newTimePhaseindex: number,
    timePhases: RemoteDiagnosticsVddGenericStructure[],
    entriesStructure: RemoteDiagnosticsVddGenericStructure,
    newStartTime: string,
    newStopTime: string,
    newMode?: string
  ): StartStopTempData {
    let entries = Number(entriesStructure.value);
    const maxNumberOfTimePhases = timePhases.length;
    const timePhasesToAdd = [
      {
        newTimePhaseindex,
        newStartTime,
        newStopTime,
        newMode,
      },
    ];

    const updatedTimePhases = timePhases.map((structure) => {
      const startTimeStructure = this.getStructure(structure, SemanticValues.STARTTIME);
      const stopTimeStructure = this.getStructure(structure, SemanticValues.STOPTIME);
      const hourMinuteStructure = this.getHourMinuteStructure(structure);
      const mode = this.getStructure(structure, SemanticValues.ENUMCHOICE)?.value;
      const startTime = this.getTimeValueFromStructure(startTimeStructure);
      const stopTime = this.getTimeValueFromStructure(stopTimeStructure);
      const startStopHourMinuteValues = this.getStartStopHourMinuteValues(
        newStartTime,
        newStopTime
      );

      // Ignore empty time phases (default value is 00:00 for stop time)
      if (stopTime !== this.defaultStartTime) {
        // divide the time phase into two phases if the new time phase is between the start and stop time of the current time phase
        if (newStartTime > startTime && newStopTime < stopTime) {
          if (entries > maxNumberOfTimePhases - 2) {
            this.isMaxNumberOfTimePhasesReached = true;
          } else {
            hourMinuteStructure.stopHourStructure.value = startStopHourMinuteValues.startHour;
            hourMinuteStructure.stopMinuteStructure.value = startStopHourMinuteValues.startMinute;

            timePhasesToAdd.push({
              newTimePhaseindex: newTimePhaseindex + 1,
              newStartTime: newStopTime,
              newStopTime: stopTime,
              newMode: mode,
            });
          }
        } else {
          // Update stop time of current time phase if start time of the new phase is between the start and stop of the current time phase
          if (newStartTime > startTime && newStartTime < stopTime) {
            hourMinuteStructure.stopHourStructure.value = startStopHourMinuteValues.startHour;
            hourMinuteStructure.stopMinuteStructure.value = startStopHourMinuteValues.startMinute;
          }

          // Update start time of current time phase if stop time of the new phase is between the start and stop of the current time phase
          if (newStopTime > startTime && newStopTime < stopTime) {
            hourMinuteStructure.startHourStructure.value = startStopHourMinuteValues.stopHour;
            hourMinuteStructure.startMinuteStructure.value = startStopHourMinuteValues.stopMinute;
          }

          // Delete the time phase if it is between the new stop and start time
          if (
            newStartTime <= startTime &&
            newStartTime < stopTime &&
            newStopTime > startTime &&
            newStopTime >= stopTime
          ) {
            entries = entries - 1;

            return this.clearTimePhase(structure);
          }
        }
      }

      return structure;
    });

    if (!this.isMaxNumberOfTimePhasesReached) {
      timePhasesToAdd.forEach((timePhase) => {
        const timePhaseToAdd = updatedTimePhases[timePhase.newTimePhaseindex];
        const modeStructure = this.getStructure(timePhaseToAdd, SemanticValues.ENUMCHOICE);
        const hourMinuteStructure = this.getHourMinuteStructure(timePhaseToAdd);
        const startStopHourMinuteValues = this.getStartStopHourMinuteValues(
          timePhase.newStartTime,
          timePhase.newStopTime
        );

        hourMinuteStructure.startHourStructure.value = startStopHourMinuteValues.startHour;
        hourMinuteStructure.startMinuteStructure.value = startStopHourMinuteValues.startMinute;
        hourMinuteStructure.stopHourStructure.value = startStopHourMinuteValues.stopHour;
        hourMinuteStructure.stopMinuteStructure.value = startStopHourMinuteValues.stopMinute;

        if (modeStructure) {
          modeStructure.value = timePhase.newMode;
        }

        entries = entries + 1;
      });
    }

    return {
      structure: this.sortTimePhasesByStartTime(updatedTimePhases),
      entriesValue: entries.toString(),
    };
  }

  deleteTimePhase(
    timePhaseIndex: number,
    timePhases: RemoteDiagnosticsVddGenericStructure[],
    entriesStructure: RemoteDiagnosticsVddGenericStructure
  ): StartStopTempData {
    let entries = Number(entriesStructure.value);

    const updatedTimePhases = timePhases.map((structure, idx) => {
      if (timePhaseIndex === idx) {
        entries = entries - 1;

        return this.clearTimePhase(structure);
      }

      return structure;
    });

    return {
      structure: this.sortTimePhasesByStartTime(updatedTimePhases),
      entriesValue: entries.toString(),
    };
  }

  clearTimePhase(
    structure: RemoteDiagnosticsVddGenericStructure
  ): RemoteDiagnosticsVddGenericStructure {
    const timePhase = structuredClone(structure);
    const modeStructure = this.getStructure(timePhase, SemanticValues.ENUMCHOICE);
    const hourMinuteStructure = this.getHourMinuteStructure(timePhase);

    hourMinuteStructure.startHourStructure.value = '0';
    hourMinuteStructure.startMinuteStructure.value = '0';
    hourMinuteStructure.stopHourStructure.value = '0';
    hourMinuteStructure.stopMinuteStructure.value = '0';

    if (modeStructure) {
      modeStructure.value = this.defaultMode;
    }

    return timePhase;
  }

  sortTimePhasesByStartTime(
    timePhases: RemoteDiagnosticsVddGenericStructure[]
  ): RemoteDiagnosticsVddGenericStructure[] {
    const empty = timePhases.filter((timePhase) => {
      const startTimeOne = this.getTimeValueFromStructure(
        this.getStructure(timePhase, SemanticValues.STARTTIME)
      );
      const stopTimeOne = this.getTimeValueFromStructure(
        this.getStructure(timePhase, SemanticValues.STOPTIME)
      );

      if (startTimeOne === this.defaultStartTime && stopTimeOne === this.defaultStartTime) {
        return timePhase;
      }
    });
    const filled = timePhases.filter((timePhase) => {
      const startTimeOne = this.getTimeValueFromStructure(
        this.getStructure(timePhase, SemanticValues.STARTTIME)
      );
      const stopTimeOne = this.getTimeValueFromStructure(
        this.getStructure(timePhase, SemanticValues.STOPTIME)
      );

      if (startTimeOne !== this.defaultStartTime || stopTimeOne !== this.defaultStartTime) {
        return timePhase;
      }
    });
    const sorted = filled.sort((a, b) => {
      const startTimeOne = this.getTimeValueFromStructure(
        this.getStructure(a, SemanticValues.STARTTIME)
      );
      const startTimeTwo = this.getTimeValueFromStructure(
        this.getStructure(b, SemanticValues.STARTTIME)
      );

      if (startTimeOne < startTimeTwo) {
        return -1;
      }
      if (startTimeOne > startTimeTwo) {
        return 1;
      }

      return 0;
    });

    return [...sorted, ...empty];
  }
}
